// src/components/dashboard/CustomStudy.tsx
// REFATORADO: Verde Militar + Sem Emojis + Ícones Lucide + ERRO CORRIGIDO

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, BookOpen, Layers, FileText } from 'lucide-react';
import CycleSelector from './customStudy/CycleSelector';
import DeckSelector from './customStudy/DeckSelector';
import SubdeckSelector from './customStudy/SubdeckSelector';
import StudySummary from './customStudy/StudySummary';
import { Cycle } from './types';
import { CustomStudyDeck, CustomStudySubdeck, SelectedItem } from './customStudy/types';
import './CustomStudy.css';

const API_BASE_URL = '/api';

interface CustomStudyProps {
    cycles: Cycle[];
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const CustomStudy: React.FC<CustomStudyProps> = ({ cycles, onShowToast }) => {
    const navigate = useNavigate();

    // Estados de seleção
    const [selectedCycleIds, setSelectedCycleIds] = useState<string[]>([]);
    const [selectedDeckIds, setSelectedDeckIds] = useState<string[]>([]);
    const [selectedSubdeckIds, setSelectedSubdeckIds] = useState<string[]>([]);

    // Estados de dados
    const [availableDecks, setAvailableDecks] = useState<CustomStudyDeck[]>([]);
    const [availableSubdecks, setAvailableSubdecks] = useState<CustomStudySubdeck[]>([]);
    const [totalCards, setTotalCards] = useState(0);
    const [loadingDecks, setLoadingDecks] = useState(false);
    const [loadingSubdecks, setLoadingSubdecks] = useState(false);

    // ========== CARREGAR DECKS QUANDO CICLOS MUDAM ==========
    useEffect(() => {
        if (selectedCycleIds.length === 0) {
            setAvailableDecks([]);
            setSelectedDeckIds([]);
            return;
        }

        loadDecks();
        // eslint-disable-next-line
    }, [selectedCycleIds]);

    // ========== CARREGAR SUBDECKS QUANDO DECKS MUDAM ==========
    useEffect(() => {
        if (selectedDeckIds.length === 0) {
            setAvailableSubdecks([]);
            setSelectedSubdeckIds([]);
            return;
        }

        loadSubdecks();
        // eslint-disable-next-line
    }, [selectedDeckIds]);

    // ========== CALCULAR TOTAL DE CARDS ==========
    useEffect(() => {
        calculateTotalCards();
        // eslint-disable-next-line
    }, [selectedSubdeckIds, availableSubdecks]);

    // ========== FUNÇÕES DE CARREGAMENTO ==========

    const loadDecks = async () => {
        setLoadingDecks(true);
        try {
            const token = localStorage.getItem('accessToken');
            const allDecks: CustomStudyDeck[] = [];

            for (const cycleId of selectedCycleIds) {

                // ✅ 1. BUSCAR DECKS OFICIAIS
                const officialResponse = await fetch(`${API_BASE_URL}/cycles/${cycleId}/decks`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (officialResponse.ok) {
                    const data = await officialResponse.json();
                    const decks = data.decks || data;

                    if (Array.isArray(decks)) {
                        const purchasedDecks = decks.filter((deck: any) =>
                            (deck.isFree || deck.purchased) && !deck.isBlocked
                        );

                        const officialDecksWithCycleId = purchasedDecks.map((deck: any) => ({
                            _id: deck._id,
                            name: deck.name,
                            cycleId: cycleId,
                            subdeckCount: deck.subdeckCount || deck.subdecks?.length || 0,
                            purchased: deck.purchased,
                            isFree: deck.isFree
                        }));

                        allDecks.push(...officialDecksWithCycleId);
                        console.log(`✅ ${purchasedDecks.length} decks oficiais acessíveis`);
                    }
                }
            }

            setAvailableDecks(allDecks);

            if (allDecks.length === 0) {
                onShowToast('Nenhum deck encontrado nos campos selecionados', 'info');
            }

        } catch (error) {
            console.error('❌ Erro ao carregar decks:', error);
            onShowToast('Erro ao carregar decks', 'error');
        } finally {
            setLoadingDecks(false);
        }
    };

    const loadSubdecks = async () => {
        setLoadingSubdecks(true);
        try {
            const token = localStorage.getItem('accessToken');

            const allSubdecks: CustomStudySubdeck[] = [];

            for (const deckId of selectedDeckIds) {
                console.log(`🔍 Carregando subdecks do deck: ${deckId}`);

                const response = await fetch(`${API_BASE_URL}/decks/${deckId}/subdecks-meta`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.status === 403) {
                    // Deck interditado ou sem acesso — pula silenciosamente
                    continue;
                }

                if (response.ok) {
                    const data = await response.json();
                    const subdecks = data.subdecks || data;

                    if (!Array.isArray(subdecks)) continue;

                    // Filtra subdecks bloqueados
                    const subdecksWithDeckId = subdecks
                        .filter((subdeck: any) => !subdeck.isBlocked)
                        .map((subdeck: any) => ({
                            _id: subdeck._id,
                            name: subdeck.name,
                            deckId: deckId,
                            cardCount: subdeck.cardCount || 0
                        }));

                    allSubdecks.push(...subdecksWithDeckId);
                }
            }

            setAvailableSubdecks(allSubdecks);

            if (allSubdecks.length === 0) {
                onShowToast('Nenhum subdeck encontrado nos decks selecionados', 'info');
            }

        } catch (error) {
            console.error('❌ Erro ao carregar subdecks:', error);
            onShowToast('Erro ao carregar subdecks', 'error');
        } finally {
            setLoadingSubdecks(false);
        }
    };

    const calculateTotalCards = () => {
        const total = availableSubdecks
            .filter(subdeck => selectedSubdeckIds.includes(subdeck._id))
            .reduce((sum, subdeck) => sum + (subdeck.cardCount || 0), 0);


        console.log('📊 Breakdown:', availableSubdecks
            .filter(s => selectedSubdeckIds.includes(s._id))
            .map(s => `${s.name}: ${s.cardCount} cards`)
        );

        setTotalCards(total);
    };

    // ========== HANDLERS ==========

    const handleCycleToggle = (cycleId: string) => {
        setSelectedCycleIds(prev => {
            const newSelection = prev.includes(cycleId)
                ? prev.filter(id => id !== cycleId)
                : [...prev, cycleId];

            console.log('🔄 Ciclos selecionados:', newSelection);
            return newSelection;
        });
    };

    const handleDeckToggle = (deckId: string) => {
        setSelectedDeckIds(prev => {
            const newSelection = prev.includes(deckId)
                ? prev.filter(id => id !== deckId)
                : [...prev, deckId];

            console.log('🔄 Decks selecionados:', newSelection);
            return newSelection;
        });
    };

    const handleSubdeckToggle = (subdeckId: string) => {
        setSelectedSubdeckIds(prev => {
            // ✅ CORREÇÃO: Usar subdeckId ao invés de deckId
            const newSelection = prev.includes(subdeckId)
                ? prev.filter(id => id !== subdeckId)
                : [...prev, subdeckId];

            console.log('🔄 Subdecks selecionados:', newSelection);
            return newSelection;
        });
    };

    const handleStartStudy = () => {
        if (selectedSubdeckIds.length === 0) {
            onShowToast('Selecione pelo menos um subdeck', 'error');
            return;
        }

        // Criar pool de estudo com todas as informações
        const studyPool: SelectedItem[] = selectedSubdeckIds.map(subdeckId => {
            const subdeck = availableSubdecks.find(s => s._id === subdeckId);
            const deck = availableDecks.find(d => d._id === subdeck?.deckId);

            return {
                cycleId: deck?.cycleId || '',
                deckId: subdeck?.deckId || '',
                subdeckId: subdeckId
            };
        });

        // Salvar no localStorage
        localStorage.setItem('customStudyPool', JSON.stringify(studyPool));
        localStorage.setItem('customStudyTotalCards', totalCards.toString());

        // Navegar para página de estudo personalizado
        sessionStorage.setItem('graia_theme_context', 'enem');
        navigate('/study/custom');
    };

    const handleReset = () => {
        setSelectedCycleIds([]);
        setSelectedDeckIds([]);
        setSelectedSubdeckIds([]);
        setAvailableDecks([]);
        setAvailableSubdecks([]);
        setTotalCards(0);
        console.log('🔄 Seleção resetada');
    };

    // ========== RENDER ==========

    return (
        <div className="custom-study-container">
            <div className="custom-study-header">
                <p className="custom-study-subtitle">
                    Monte sua própria sessão de estudos combinando conteúdos livremente
                </p>
                {(selectedCycleIds.length > 0 || selectedDeckIds.length > 0 || selectedSubdeckIds.length > 0) && (
                    <button className="btn-reset" onClick={handleReset}>
                        <RefreshCw size={16} />
                        Recomeçar
                    </button>
                )}
            </div>

            {/* Progresso Visual */}
            <div className="selection-progress">
                <div className={`progress-step ${selectedCycleIds.length > 0 ? 'active' : ''}`}>
                    <div className="step-icon">
                        <BookOpen size={20} />
                    </div>
                    <div className="step-label">Campos de Estudo</div>
                </div>
                <div className="progress-connector"></div>
                <div className={`progress-step ${selectedDeckIds.length > 0 ? 'active' : ''}`}>
                    <div className="step-icon">
                        <Layers size={20} />
                    </div>
                    <div className="step-label">Decks</div>
                </div>
                <div className="progress-connector"></div>
                <div className={`progress-step ${selectedSubdeckIds.length > 0 ? 'active' : ''}`}>
                    <div className="step-icon">
                        <FileText size={20} />
                    </div>
                    <div className="step-label">Subdecks</div>
                </div>
            </div>

            {/* Passo 1: Selecionar Campos */}
            <CycleSelector
                cycles={cycles}
                selectedCycleIds={selectedCycleIds}
                onToggle={handleCycleToggle}
            />

            {/* Passo 2: Selecionar Decks */}
            {selectedCycleIds.length > 0 && (
                <DeckSelector
                    decks={availableDecks}
                    selectedDeckIds={selectedDeckIds}
                    cycles={cycles}
                    onToggle={handleDeckToggle}
                    loading={loadingDecks}
                />
            )}

            {/* Passo 3: Selecionar Subdecks */}
            {selectedDeckIds.length > 0 && (
                <SubdeckSelector
                    subdecks={availableSubdecks}
                    selectedSubdeckIds={selectedSubdeckIds}
                    decks={availableDecks}
                    onToggle={handleSubdeckToggle}
                    loading={loadingSubdecks}
                />
            )}

            {/* Resumo e Ação */}
            {selectedSubdeckIds.length > 0 && (
                <StudySummary
                    cycleCount={selectedCycleIds.length}
                    deckCount={selectedDeckIds.length}
                    subdeckCount={selectedSubdeckIds.length}
                    totalCards={totalCards}
                    onStartStudy={handleStartStudy}
                />
            )}
        </div>
    );
};

export default CustomStudy;